import { SUPABASE_URL, SUPABASE_ANON_KEY } from '@/config';
import { supabase } from '@/lib/supabase';

export type WpConnectionResult = {
  connected: boolean;
  seoPlugin?: 'yoast' | 'rankmath' | 'none';
  error?: string;
};

export type WpPostInfo = {
  postId: number;
  title: string;
  slug: string;
  content: string;
  seoTitle: string;
  seoDescription: string;
  permalink: string;
  postType: string;
};

export type WpVerification = {
  expected: string;
  actual: string;
  verified: boolean;
};

export type WpApplyResult = {
  success: boolean;
  postId: number;
  changes: string[];
  verification?: Record<string, WpVerification>;
  error?: string;
};

async function invokeWpFunction(body: Record<string, unknown>): Promise<any> {
  const {
    data: { session },
  } = await supabase.auth.getSession();
  const userId = session?.user?.id;

  const res = await fetch(`${SUPABASE_URL}/functions/v1/wordpress-api`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${session?.access_token ?? ''}`,
      apikey: SUPABASE_ANON_KEY,
    },
    body: JSON.stringify({ ...body, userId }),
  });

  const data = await res.json();

  if (!res.ok) {
    throw new Error(data.error || `WordPress API error ${res.status}`);
  }

  return data;
}

const wordpressService = {
  async connect(params: {
    projectId: string;
    wpSiteUrl: string;
    wpUsername: string;
    wpAppPassword: string;
  }): Promise<WpConnectionResult> {
    return invokeWpFunction({
      action: 'saveCredentials',
      ...params,
    });
  },

  async disconnect(projectId: string): Promise<void> {
    await invokeWpFunction({
      action: 'disconnect',
      projectId,
    });
  },

  async syncPages(projectId: string): Promise<{ synced: number; total: number }> {
    return invokeWpFunction({
      action: 'syncPages',
      projectId,
    });
  },

  async syncSitemap(
    projectId: string,
    siteUrl: string
  ): Promise<{ synced: number; total: number; error?: string }> {
    return invokeWpFunction({
      action: 'syncSitemap',
      projectId,
      siteUrl,
    });
  },

  async findPostByUrl(projectId: string, pageUrl: string): Promise<WpPostInfo | null> {
    try {
      const result = await invokeWpFunction({
        action: 'findPostByUrl',
        projectId,
        pageUrl,
      });
      // Edge function returns { error: ... } as 200 when page not found
      if (result.error || !result.postId) return null;
      return result;
    } catch {
      return null;
    }
  },

  async applyChanges(params: {
    projectId: string;
    postId: number;
    postType?: string;
    opportunityId?: string;
    pageUrl?: string;
    keywordId?: string;
    beforeMetrics?: {
      position: number | null;
      ctr: number | null;
      clicks: number | null;
      impressions: number | null;
    };
    changes: {
      seoTitle?: string;
      seoDescription?: string;
      slug?: string;
      content?: string;
      schema?: string;
    };
  }): Promise<WpApplyResult> {
    return invokeWpFunction({
      action: 'applyChanges',
      ...params,
    });
  },
};

export default wordpressService;
